import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import Cropper, { Area, Point } from 'react-easy-crop';
import DebouncedInput from './DebouncedInput';
import { db, doc, updateDoc, handleFirestoreError, OperationType } from '../firebase';
import { CURRENCIES } from '@/src/constants';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgradeClick: () => void;
  userProfile: any;
  userId: string;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose, onUpgradeClick, userProfile, userId }) => {
  const [formData, setFormData] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (userProfile) {
      setFormData({
        name: userProfile.name || '',
        agentName: userProfile.agentName || '',
        email: userProfile.email || '',
        phone: userProfile.phone || '',
        location: userProfile.location || '',
        photoURL: userProfile.photoURL || '',
        portfolio: userProfile.portfolio || '',
        website: userProfile.website || '',
        industry: userProfile.industry || '',
        companySize: userProfile.companySize || '',
        description: userProfile.description || '',
        preferredCurrency: userProfile.preferredCurrency || 'USD',
        preferredLanguage: userProfile.preferredLanguage || 'English',
        linkedin: userProfile.linkedin || '',
        twitter: userProfile.twitter || '',
      });
    }
  }, [userProfile, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: Record<string, any>) => ({ ...prev, [name]: value }));
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsCameraOpen(true);
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setError("Could not access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg');
        setImageToCrop(dataUrl);
        stopCamera();
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageToCrop(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<string | null> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return canvas.toDataURL('image/jpeg');
  };

  const handleCropSave = async () => {
    if (imageToCrop && croppedAreaPixels) {
      try {
        const croppedImage = await getCroppedImg(imageToCrop, croppedAreaPixels);
        if (croppedImage) {
          setFormData((prev: Record<string, any>) => ({ ...prev, photoURL: croppedImage }));
          setImageToCrop(null);
        }
      } catch (e) {
        console.error(e);
        setError("Failed to crop image. Please try again.");
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const updateData = {
        ...formData,
        updatedAt: new Date().toISOString(),
      };
      
      await updateDoc(doc(db, 'users', userId), updateData);
      setIsSaving(false);
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      setError('Failed to update profile. Check connection.');
      handleFirestoreError(err, OperationType.UPDATE, `users/${userId}`);
    }
  };

  const isCompany = userProfile?.userType === 'company';

  return (
    <AnimatePresence>
      {isOpen && (
        <div key="edit-profile-modal-wrapper" className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/75"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="relative w-full max-w-2xl bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden flex flex-col max-h-[90vh] text-white"
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-white/[0.06] bg-[#322D29] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded bg-[#F26522] flex items-center justify-center text-white">
                  <i className="fas fa-user-edit text-xs"></i>
                </div>
                <div>
                  <h2 className="text-sm font-bold uppercase tracking-widest text-white font-heading">
                    Edit Profile
                  </h2>
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#F26522] font-heading mt-0.5">
                    {isCompany ? 'ENTERPRISE PROFILE' : 'INDIVIDUAL PROFILE'}
                  </p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Profile Picture Section */}
                <div className="flex flex-col items-center gap-3 py-1">
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-full border-2 border-white/10 overflow-hidden bg-[#322D29] flex items-center justify-center">
                      {formData.photoURL ? (
                        <img 
                          src={formData.photoURL} 
                          alt={isCompany ? "Logo" : "Profile"} 
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <i className={`fas ${isCompany ? 'fa-building' : 'fa-user'} text-white/30 text-3xl`}></i>
                      )}
                    </div>
                    
                    <div className="absolute -bottom-1 -right-1 flex gap-1">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-8 h-8 rounded-full bg-[#322D29] border border-white/10 flex items-center justify-center text-white/70 hover:text-[#F26522] transition-colors cursor-pointer"
                        title="Upload from gallery"
                      >
                        <i className="fas fa-image text-xs"></i>
                      </button>
                      <button
                        type="button"
                        onClick={startCamera}
                        className="w-8 h-8 rounded-full bg-[#F26522] flex items-center justify-center text-white hover:bg-[#F26522]/90 transition-colors cursor-pointer"
                        title="Take photo"
                      >
                        <i className="fas fa-camera text-xs"></i>
                      </button>
                    </div>
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">
                    {isCompany ? 'Company Logo' : 'Profile Picture'}
                  </p>
                  
                  <input 
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                  />
                </div>

                {/* Camera Interface */}
                {isCameraOpen && (
                  <div className="fixed inset-0 z-[200] bg-black/90 flex flex-col items-center justify-center p-4">
                    <div className="relative w-full max-w-lg aspect-video bg-[#322D29] rounded-xl overflow-hidden border border-white/10">
                      <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-6 inset-x-0 flex justify-center gap-6">
                        <button
                          type="button"
                          onClick={stopCamera}
                          className="w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all cursor-pointer"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                        <button
                          type="button"
                          onClick={capturePhoto}
                          className="w-14 h-14 rounded-full bg-[#F26522] border-2 border-white flex items-center justify-center text-white hover:scale-105 transition-all cursor-pointer"
                        >
                          <i className="fas fa-camera text-lg"></i>
                        </button>
                      </div>
                    </div>
                    <p className="mt-4 text-white/60 text-[10px] font-bold uppercase tracking-widest">Position yourself in the frame</p>
                  </div>
                )}

                {/* Cropping Interface */}
                {imageToCrop && (
                  <div className="fixed inset-0 z-[200] bg-black/90 flex flex-col p-4">
                    <div className="relative flex-1 bg-[#322D29] rounded-xl overflow-hidden border border-white/10">
                      <Cropper
                        image={imageToCrop}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                      />
                    </div>
                    <div className="p-6 bg-[#322D29] border border-white/10 mt-4 rounded-xl flex flex-col gap-4">
                      <div className="flex items-center gap-4">
                        <span className="text-white text-[10px] uppercase font-bold tracking-widest">Zoom</span>
                        <input
                          type="range"
                          value={zoom}
                          min={1}
                          max={3}
                          step={0.1}
                          aria-labelledby="Zoom"
                          onChange={(e) => setZoom(Number(e.target.value))}
                          className="flex-1 accent-[#F26522]"
                        />
                      </div>
                      <div className="flex gap-4">
                        <button
                          onClick={() => setImageToCrop(null)}
                          className="flex-1 py-3 border border-white/10 rounded-lg text-white text-[10px] font-bold uppercase tracking-widest hover:bg-white/5 transition-all cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCropSave}
                          className="flex-1 py-3 bg-[#F26522] rounded-lg text-white text-[10px] font-bold uppercase tracking-widest hover:bg-[#F26522]/90 transition-all cursor-pointer"
                        >
                          Apply Crop
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <canvas ref={canvasRef} className="hidden" />

                {/* Identity Section */}
                <div className="space-y-4">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40 border-b border-white/[0.06] pb-2 font-heading">Identity Matrix</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">
                        {isCompany ? 'Company Name' : 'Full Name'}
                      </label>
                      <input 
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                        defaultValue={formData.name || ''}
                        key={`name-${formData.name}`}
                        onChange={handleChange}
                      />
                    </div>

                    {isCompany && (
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Representative Name</label>
                        <input 
                          type="text"
                          name="agentName"
                          required
                          className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                          defaultValue={formData.agentName || ''}
                          key={`agentName-${formData.agentName}`}
                          onChange={handleChange}
                        />
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Contact Email</label>
                      <input 
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                        defaultValue={formData.email || ''}
                        key={`email-${formData.email}`}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Phone Link</label>
                      <input 
                        type="tel"
                        name="phone"
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                        defaultValue={formData.phone || ''}
                        key={`phone-${formData.phone}`}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Location</label>
                      <DebouncedInput 
                        type="text"
                        name="location"
                        placeholder="City, Country"
                        className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                        value={formData.location || ''}
                        onChange={(val) => setFormData((prev: Record<string, any>) => ({ ...prev, location: val }))}
                      />
                    </div>

                    <div className="space-y-1.5 md:col-span-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Profile Picture URL</label>
                      <div className="flex gap-3 items-center">
                        <div className="w-11 h-11 rounded-lg bg-[#322D29] border border-white/[0.08] flex items-center justify-center overflow-hidden shrink-0">
                          {formData.photoURL ? (
                            <img src={formData.photoURL} alt="Preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                          ) : (
                            <i className="fas fa-user text-white/30"></i>
                          )}
                        </div>
                        <input 
                          type="url"
                          name="photoURL"
                          placeholder="https://example.com/photo.jpg"
                          className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                          defaultValue={formData.photoURL || ''}
                          key={`photoURL-${formData.photoURL}`}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Professional Section */}
                <div className="space-y-4">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40 border-b border-white/[0.06] pb-2 font-heading">Professional Directives</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {isCompany && (
                      <>
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Industry Sector</label>
                          <DebouncedInput 
                            type="text"
                            name="industry"
                            placeholder="e.g. Technology, Finance"
                            className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                            value={formData.industry || ''}
                            onChange={(val) => setFormData((prev: Record<string, any>) => ({ ...prev, industry: val }))}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Entity Size</label>
                          <select 
                            name="companySize"
                            className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors appearance-none font-dm cursor-pointer"
                            defaultValue={formData.companySize || ''}
                            key={`companySize-${formData.companySize}`}
                            onChange={handleChange}
                          >
                            <option value="">Select Size</option>
                            <option value="1-10">1-10 Operatives</option>
                            <option value="11-50">11-50 Operatives</option>
                            <option value="51-200">51-200 Operatives</option>
                            <option value="201-500">201-500 Operatives</option>
                            <option value="500+">500+ Operatives</option>
                          </select>
                        </div>
                        <div className="space-y-1.5 md:col-span-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Digital Headquarters (URL)</label>
                          <input 
                            type="url"
                            name="website"
                            placeholder="https://entity.com"
                            className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                            defaultValue={formData.website || ''}
                            key={`website-${formData.website}`}
                            onChange={handleChange}
                          />
                        </div>
                      </>
                    )}

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Preferred Currency</label>
                      <select 
                        name="preferredCurrency"
                        className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors appearance-none font-dm cursor-pointer"
                        defaultValue={formData.preferredCurrency || 'USD'}
                        key={`preferredCurrency-${formData.preferredCurrency}`}
                        onChange={handleChange}
                      >
                        {CURRENCIES.map((c, i) => {
                          if (!c || !c.code) return null;
                          return (
                          <option key={`currency-${c.code}`} value={c.code} className="bg-[#322D29] text-white">{c.code} - {c.name}</option>
                        )})}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Interface Language</label>
                      <select 
                        name="preferredLanguage"
                        className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors appearance-none font-dm cursor-pointer"
                        defaultValue={formData.preferredLanguage || 'English'}
                        key={`preferredLanguage-${formData.preferredLanguage}`}
                        onChange={handleChange}
                      >
                        <option value="English" className="bg-[#322D29] text-white">English</option>
                        <option value="Spanish" className="bg-[#322D29] text-white">Spanish</option>
                        <option value="French" className="bg-[#322D29] text-white">French</option>
                        <option value="German" className="bg-[#322D29] text-white">German</option>
                        <option value="Japanese" className="bg-[#322D29] text-white">Japanese</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">
                      {isCompany ? 'Entity Mission Statement' : 'Operative Bio'}
                    </label>
                    <textarea 
                      name="description"
                      rows={4}
                      placeholder="Describe your strategic objectives..."
                      className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors resize-none font-dm"
                      defaultValue={formData.description || ''}
                      key={`description-${formData.description}`}
                      onChange={handleChange}
                    />
                  </div>

                  {!isCompany && (
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Individual Portfolio Text</label>
                      <textarea 
                        name="portfolio"
                        rows={4}
                        placeholder="Showcase your professional achievements and portfolio..."
                        className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors resize-none font-dm"
                        defaultValue={formData.portfolio || ''}
                        key={`portfolio-${formData.portfolio}`}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                </div>

                {/* Social Section */}
                <div className="space-y-4">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40 border-b border-white/[0.06] pb-2 font-heading">Intelligence Networks</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">LinkedIn Profile</label>
                      <div className="relative">
                        <i className="fab fa-linkedin absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs"></i>
                        <input 
                          type="url"
                          name="linkedin"
                          placeholder="https://linkedin.com/in/..."
                          className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                          defaultValue={formData.linkedin || ''}
                          key={`linkedin-${formData.linkedin}`}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">X / Twitter Handle</label>
                      <div className="relative">
                        <i className="fab fa-twitter absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs"></i>
                        <input 
                          type="text"
                          name="twitter"
                          placeholder="@handle"
                          className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-xs font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                          defaultValue={formData.twitter || ''}
                          key={`twitter-${formData.twitter}`}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-950/40 border border-red-500/40 rounded-lg flex items-center gap-2.5">
                    <i className="fas fa-exclamation-triangle text-red-400 text-xs"></i>
                    <span className="text-[10px] font-medium uppercase text-red-200 tracking-wider">{error}</span>
                  </div>
                )}
              </form>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-white/[0.06] bg-[#322D29] flex items-center justify-between shrink-0">
              <button
                type="button"
                onClick={onUpgradeClick}
                className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#F26522] hover:text-[#F26522]/80 transition-colors group cursor-pointer"
              >
                <i className="fas fa-crown group-hover:scale-110 transition-transform"></i>
                Upgrade Account
              </button>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 border border-white/10 hover:bg-white/5 text-[10px] font-bold uppercase tracking-widest rounded-lg transition-colors cursor-pointer text-white/80"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="px-6 py-2.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[10px] font-bold uppercase tracking-widest rounded-lg transition-colors flex items-center gap-2 cursor-pointer font-heading disabled:opacity-50"
                >
                  {isSaving ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin text-xs"></i>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save text-xs"></i>
                      Commit Configuration
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EditProfileModal;
